/**
 * The playbook wire editor.
 *
 * A playbook is a graph of typed nodes joined by wires. This screen draws it, lets an operator
 * rearrange and rewire it, and — the part that matters — validates continuously against the
 * server's own validator rather than a reimplementation. A client-side validator would drift,
 * and the one thing worse than a playbook that fails validation is one the editor said was fine.
 *
 * Nodes carrying an action with a blast radius wider than a single host are drawn with a warning
 * border until every path to them passes an approval gate. That is the mistake this editor exists
 * to prevent: an author gates the obvious path and leaves a parallel arm open, and because the
 * gate is visibly on the canvas, review passes.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api, type ActionSpec, type Playbook, type PlaybookNode } from '../api'
import { useSession } from '../App'
import { Async, Card, Chip, Mono, Note, useAsync, useToast } from '../components/ui'

const NODE_W = 176
const NODE_H = 58

const NODE_KINDS: { kind: string; label: string; hint: string }[] = [
  { kind: 'action', label: 'Action', hint: 'Do something from the catalog' },
  { kind: 'decision', label: 'Decision', hint: 'Branch on the incident' },
  { kind: 'approval', label: 'Approval', hint: 'Wait for a person to decide' },
  { kind: 'parallel', label: 'Parallel', hint: 'Fan out along every wire' },
  { kind: 'join', label: 'Join', hint: 'Wait for every branch' },
  { kind: 'wait', label: 'Wait', hint: 'Pause for a fixed time' },
  { kind: 'end', label: 'End', hint: 'Record an outcome' },
]

function nodeTone(kind: string): { fill: string; stroke: string } {
  switch (kind) {
    case 'start':
      return { fill: 'var(--ok-bg)', stroke: 'var(--ok)' }
    case 'end':
      return { fill: 'var(--surface-3)', stroke: 'var(--border-strong)' }
    case 'approval':
      return { fill: 'var(--warn-bg)', stroke: 'var(--warn)' }
    case 'decision':
      return { fill: 'var(--accent-soft)', stroke: 'var(--accent)' }
    case 'parallel':
    case 'join':
      return { fill: 'var(--surface-2)', stroke: 'var(--border-strong)' }
    case 'wait':
      return { fill: 'var(--surface-2)', stroke: 'var(--text-faint)' }
    default:
      return { fill: 'var(--surface)', stroke: 'var(--border-strong)' }
  }
}

/** A cubic wire between two node edges, horizontal-first so branches read left to right. */
function wirePath(from: { x: number; y: number }, to: { x: number; y: number }): string {
  const x1 = from.x + NODE_W
  const y1 = from.y + NODE_H / 2
  const x2 = to.x
  const y2 = to.y + NODE_H / 2
  const dx = Math.max(40, Math.abs(x2 - x1) * 0.45)
  return `M${x1},${y1} C${x1 + dx},${y1} ${x2 - dx},${y2} ${x2},${y2}`
}

export default function PlaybookEditor() {
  const { id = '' } = useParams()
  const { can } = useSession()
  const toast = useToast()
  const editable = can('configure_playbooks')

  const loaded = useAsync<Playbook>(() => api.get<Playbook>(`/playbooks/${id}`), [id])
  const actions = useAsync<ActionSpec[]>(() => api.get<ActionSpec[]>('/actions'), [])

  const [pb, setPb] = useState<Playbook | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [linkFrom, setLinkFrom] = useState<string | null>(null)
  const [validation, setValidation] = useState<{ errors: string[]; advisories: string[]; max_blast_radius: string } | null>(null)
  const [dirty, setDirty] = useState(false)
  const [busy, setBusy] = useState(false)

  // View transform.
  const [view, setView] = useState({ x: 20, y: 20, k: 1 })
  const svgRef = useRef<SVGSVGElement | null>(null)
  const drag = useRef<{ kind: 'pan' | 'node'; id?: string; ox: number; oy: number } | null>(null)

  useEffect(() => {
    if (loaded.data) setPb(loaded.data)
  }, [loaded.data])

  // Validate against the server whenever the graph changes. Debounced, because dragging a node
  // emits a change per frame and the validator is not what should be under that load.
  useEffect(() => {
    if (!pb) return
    const h = window.setTimeout(() => {
      api
        .post<{ errors: string[]; advisories: string[]; max_blast_radius: string }>('/playbooks/validate', pb)
        .then(setValidation)
        .catch(() => setValidation(null))
    }, 400)
    return () => window.clearTimeout(h)
  }, [pb])

  const actionMap = useMemo(() => {
    const m = new Map<string, ActionSpec>()
    for (const a of actions.data ?? []) m.set(a.id, a)
    return m
  }, [actions.data])

  /** Nodes whose action needs approval and can be reached without one. */
  const ungated = useMemo(() => {
    if (!pb) return new Set<string>()
    const needsGate = new Set(
      pb.nodes
        .filter((n) => n.action_id && actionMap.get(n.action_id)?.requires_approval)
        .map((n) => n.id),
    )
    if (needsGate.size === 0) return new Set<string>()

    // Walk from Start, refusing to pass through an approval node. Anything still reachable is
    // reachable ungated — the same rule the server applies.
    const start = pb.nodes.find((n) => n.kind === 'start')
    const out = new Set<string>()
    if (!start) return needsGate
    const seen = new Set<string>()
    const queue = [start.id]
    while (queue.length) {
      const cur = queue.shift()
      if (!cur || seen.has(cur)) continue
      seen.add(cur)
      if (needsGate.has(cur)) out.add(cur)
      const node = pb.nodes.find((n) => n.id === cur)
      if (node?.kind === 'approval' && cur !== start.id) continue
      for (const e of pb.edges.filter((e) => e.from === cur)) queue.push(e.to)
    }
    return out
  }, [pb, actionMap])

  const toWorld = useCallback(
    (clientX: number, clientY: number) => {
      const rect = svgRef.current?.getBoundingClientRect()
      if (!rect) return { x: 0, y: 0 }
      return { x: (clientX - rect.left - view.x) / view.k, y: (clientY - rect.top - view.y) / view.k }
    },
    [view],
  )

  function mutate(fn: (p: Playbook) => Playbook) {
    setPb((prev) => (prev ? fn(prev) : prev))
    setDirty(true)
  }

  function addNode(kind: string) {
    if (!pb) return
    const n = pb.nodes.length
    const base: PlaybookNode = {
      id: `${kind}_${Date.now().toString(36)}`,
      label: NODE_KINDS.find((k) => k.kind === kind)?.label ?? kind,
      kind,
      position: { x: 120 + (n % 6) * 200, y: 120 + Math.floor(n / 6) * 120 },
    }
    if (kind === 'action') {
      base.action_id = 'enrich.intel_lookup'
      base.params = {}
      base.continue_on_error = false
    }
    if (kind === 'decision') {
      base.question = 'Is this malicious?'
      base.branches = ['yes', 'no']
      base.expression = 'score>=70'
    }
    if (kind === 'approval') {
      base.prompt = 'Approve this action?'
      base.approvers = ['soc_lead']
      base.timeout_ms = 900_000
      base.on_timeout = 'halt'
    }
    if (kind === 'wait') base.duration_ms = 300_000
    if (kind === 'end') base.outcome = 'resolved'
    mutate((p) => ({ ...p, nodes: [...p.nodes, base] }))
    setSelected(base.id)
  }

  function deleteNode(nodeId: string) {
    mutate((p) => ({
      ...p,
      nodes: p.nodes.filter((n) => n.id !== nodeId),
      edges: p.edges.filter((e) => e.from !== nodeId && e.to !== nodeId),
    }))
    setSelected(null)
  }

  function connect(from: string, to: string) {
    if (from === to) return
    mutate((p) =>
      p.edges.some((e) => e.from === from && e.to === to) ? p : { ...p, edges: [...p.edges, { from, to }] },
    )
  }

  function save() {
    if (!pb) return
    setBusy(true)
    api
      .put<Playbook>('/playbooks', pb)
      .then((saved) => {
        setPb(saved)
        setDirty(false)
        toast(
          saved.errors.length ? 'error' : 'ok',
          saved.errors.length ? `Saved, but ${saved.errors.length} problem(s) remain` : 'Playbook saved',
        )
      })
      .catch((e: Error) => toast('error', e.message))
      .finally(() => setBusy(false))
  }

  return (
    <Async state={loaded}>
      {() =>
        !pb ? null : (
          <>
            <div className="page-head">
              <div style={{ minWidth: 0 }}>
                <div className="row" style={{ gap: 8, marginBottom: 4 }}>
                  <Link to="/playbooks" className="small">
                    ← Playbooks
                  </Link>
                  <Mono>{pb.id}</Mono>
                  <Chip tone="outline">v{pb.version}</Chip>
                </div>
                <h1>{pb.name}</h1>
                <div className="sub">{pb.description}</div>
              </div>
              <div className="actions">
                <Chip
                  tone={
                    validation?.max_blast_radius === 'estate'
                      ? 'danger'
                      : validation?.max_blast_radius === 'group'
                        ? 'warn'
                        : 'neutral'
                  }
                  title="The widest effect any action in this playbook can have"
                >
                  blast radius: {validation?.max_blast_radius ?? pb.max_blast_radius}
                </Chip>
                {editable && (
                  <button className="primary" onClick={save} disabled={busy || !dirty}>
                    {busy ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
                  </button>
                )}
              </div>
            </div>

            {validation && validation.errors.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <Note tone="danger">
                  <strong>This playbook will not run:</strong>
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                    {validation.errors.map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                </Note>
              </div>
            )}
            {validation && validation.advisories.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <Note tone="warn">
                  <strong>Worth considering:</strong>
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                    {validation.advisories.map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                </Note>
              </div>
            )}

            <div className="canvas-wrap">
              {editable && (
                <div className="canvas-tools">
                  {NODE_KINDS.map((k) => (
                    <button key={k.kind} className="sm" title={k.hint} onClick={() => addNode(k.kind)}>
                      + {k.label}
                    </button>
                  ))}
                  <button className="sm ghost" onClick={() => setView({ x: 20, y: 20, k: 1 })}>
                    Reset view
                  </button>
                </div>
              )}

              <svg
                ref={svgRef}
                className={drag.current ? 'dragging' : ''}
                onMouseDown={(e) => {
                  if (e.target === e.currentTarget) {
                    drag.current = { kind: 'pan', ox: e.clientX - view.x, oy: e.clientY - view.y }
                    setSelected(null)
                    setLinkFrom(null)
                  }
                }}
                onMouseMove={(e) => {
                  const d = drag.current
                  if (!d) return
                  if (d.kind === 'pan') {
                    setView((v) => ({ ...v, x: e.clientX - d.ox, y: e.clientY - d.oy }))
                  } else if (d.id) {
                    const w = toWorld(e.clientX, e.clientY)
                    const nid = d.id
                    setPb((prev) =>
                      prev
                        ? {
                            ...prev,
                            nodes: prev.nodes.map((n) =>
                              n.id === nid ? { ...n, position: { x: w.x - d.ox, y: w.y - d.oy } } : n,
                            ),
                          }
                        : prev,
                    )
                  }
                }}
                onMouseUp={() => {
                  if (drag.current?.kind === 'node') setDirty(true)
                  drag.current = null
                }}
                onMouseLeave={() => {
                  drag.current = null
                }}
                onWheel={(e) => {
                  const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1
                  setView((v) => ({ ...v, k: Math.min(2.2, Math.max(0.35, v.k * factor)) }))
                }}
              >
                <defs>
                  <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--border-strong)" />
                  </marker>
                  <pattern id="grid" width="24" height="24" patternUnits="userSpaceOnUse">
                    <circle cx="1" cy="1" r="1" fill="var(--border)" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid)" opacity="0.6" />

                <g transform={`translate(${view.x},${view.y}) scale(${view.k})`}>
                  {pb.edges.map((e, i) => {
                    const from = pb.nodes.find((n) => n.id === e.from)
                    const to = pb.nodes.find((n) => n.id === e.to)
                    if (!from || !to) return null
                    const mid = {
                      x: (from.position.x + NODE_W + to.position.x) / 2,
                      y: (from.position.y + to.position.y) / 2 + NODE_H / 2,
                    }
                    return (
                      <g key={`${e.from}-${e.to}-${i}`}>
                        <path
                          d={wirePath(from.position, to.position)}
                          fill="none"
                          stroke="var(--border-strong)"
                          strokeWidth={1.6}
                          markerEnd="url(#arrow)"
                        />
                        {e.branch && (
                          <>
                            <rect x={mid.x - 22} y={mid.y - 9} width={44} height={17} rx={8} fill="var(--surface)" stroke="var(--border)" />
                            <text x={mid.x} y={mid.y + 3} textAnchor="middle" fontSize="10" fill="var(--text-muted)">
                              {e.branch}
                            </text>
                          </>
                        )}
                        {editable && (
                          <circle
                            cx={mid.x}
                            cy={mid.y + (e.branch ? 14 : 0)}
                            r={6}
                            fill="transparent"
                            style={{ cursor: 'pointer' }}
                            onClick={() =>
                              mutate((p) => ({ ...p, edges: p.edges.filter((_, idx) => idx !== i) }))
                            }
                          >
                            <title>Remove this wire</title>
                          </circle>
                        )}
                      </g>
                    )
                  })}

                  {pb.nodes.map((n) => {
                    const tone = nodeTone(n.kind)
                    const spec = n.action_id ? actionMap.get(n.action_id) : undefined
                    const risky = ungated.has(n.id)
                    const isSelected = selected === n.id
                    return (
                      <g
                        key={n.id}
                        transform={`translate(${n.position.x},${n.position.y})`}
                        style={{ cursor: editable ? 'move' : 'pointer' }}
                        onMouseDown={(e) => {
                          e.stopPropagation()
                          setSelected(n.id)
                          if (!editable) return
                          const w = toWorld(e.clientX, e.clientY)
                          drag.current = { kind: 'node', id: n.id, ox: w.x - n.position.x, oy: w.y - n.position.y }
                        }}
                        onClick={(e) => {
                          e.stopPropagation()
                          if (linkFrom && linkFrom !== n.id) {
                            connect(linkFrom, n.id)
                            setLinkFrom(null)
                          }
                        }}
                      >
                        <rect
                          width={NODE_W}
                          height={NODE_H}
                          rx={9}
                          fill={tone.fill}
                          stroke={risky ? 'var(--danger)' : isSelected ? 'var(--accent)' : tone.stroke}
                          strokeWidth={risky || isSelected ? 2.2 : 1.3}
                          strokeDasharray={risky ? '5 3' : undefined}
                        />
                        <text x={11} y={19} fontSize="9.5" fill="var(--text-faint)" style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                          {n.kind}
                          {spec ? ` · ${spec.blast_radius}` : ''}
                        </text>
                        <text x={11} y={35} fontSize="12.5" fill="var(--text)" fontWeight={550}>
                          {n.label.length > 22 ? `${n.label.slice(0, 21)}…` : n.label}
                        </text>
                        {n.action_id && (
                          <text x={11} y={49} fontSize="10" fill="var(--text-muted)" fontFamily="var(--mono)">
                            {n.action_id.length > 26 ? `${n.action_id.slice(0, 25)}…` : n.action_id}
                          </text>
                        )}
                        {risky && (
                          <title>
                            This action has a {spec?.blast_radius} blast radius and can be reached without
                            passing an approval gate.
                          </title>
                        )}
                        {editable && (
                          <circle
                            cx={NODE_W}
                            cy={NODE_H / 2}
                            r={6}
                            fill={linkFrom === n.id ? 'var(--accent)' : 'var(--surface)'}
                            stroke="var(--border-strong)"
                            style={{ cursor: 'crosshair' }}
                            onMouseDown={(e) => {
                              e.stopPropagation()
                              setLinkFrom(n.id)
                            }}
                          >
                            <title>Drag from here, then click a node to wire them</title>
                          </circle>
                        )}
                      </g>
                    )
                  })}
                </g>
              </svg>

              <div className="canvas-legend">
                <span className="k">
                  <span className="sw" style={{ background: 'var(--warn-bg)', borderColor: 'var(--warn)' }} /> approval
                </span>
                <span className="k">
                  <span className="sw" style={{ background: 'var(--accent-soft)', borderColor: 'var(--accent)' }} /> decision
                </span>
                <span className="k">
                  <span className="sw" style={{ borderColor: 'var(--danger)', borderStyle: 'dashed' }} /> reachable ungated
                </span>
                {linkFrom && <span className="k">Click a node to wire from {linkFrom}</span>}
                <span className="k faint">scroll to zoom · drag the background to pan</span>
              </div>

              {selected && (
                <Inspector
                  node={pb.nodes.find((n) => n.id === selected) ?? null}
                  actions={actions.data ?? []}
                  editable={editable}
                  edges={pb.edges.filter((e) => e.from === selected)}
                  onChange={(patch) =>
                    mutate((p) => ({
                      ...p,
                      nodes: p.nodes.map((n) => (n.id === selected ? { ...n, ...patch } : n)),
                    }))
                  }
                  onBranchChange={(to, branch) =>
                    mutate((p) => ({
                      ...p,
                      edges: p.edges.map((e) =>
                        e.from === selected && e.to === to ? { ...e, branch: branch || null } : e,
                      ),
                    }))
                  }
                  onDelete={() => deleteNode(selected)}
                  onClose={() => setSelected(null)}
                />
              )}
            </div>

            <div style={{ marginTop: 14 }}>
              <Card title="Trigger" subtitle="What causes this playbook to run">
                <div className="grid cols-3">
                  <div className="field">
                    <label>Minimum incident severity</label>
                    <select
                      disabled={!editable}
                      value={pb.trigger.min_severity ?? ''}
                      onChange={(e) =>
                        mutate((p) => ({
                          ...p,
                          trigger: { ...p.trigger, min_severity: (e.target.value || null) as never },
                        }))
                      }
                    >
                      <option value="">any</option>
                      {['info', 'low', 'medium', 'high', 'critical'].map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="field">
                    <label>Runs automatically</label>
                    <label className="row" style={{ gap: 7, marginTop: 6 }}>
                      <input
                        type="checkbox"
                        disabled={!editable}
                        checked={pb.trigger.automatic}
                        onChange={(e) =>
                          mutate((p) => ({ ...p, trigger: { ...p.trigger, automatic: e.target.checked } }))
                        }
                      />
                      <span className="small muted">Start without waiting for a person</span>
                    </label>
                  </div>
                  <div className="field">
                    <label>Enabled</label>
                    <label className="row" style={{ gap: 7, marginTop: 6 }}>
                      <input
                        type="checkbox"
                        disabled={!editable}
                        checked={pb.enabled}
                        onChange={(e) => mutate((p) => ({ ...p, enabled: e.target.checked }))}
                      />
                      <span className="small muted">Available for selection</span>
                    </label>
                  </div>
                </div>
                <dl className="kv">
                  <dt>Techniques</dt>
                  <dd>{pb.trigger.techniques.join(', ') || '—'}</dd>
                  <dt>Rules</dt>
                  <dd className="mono small">{pb.trigger.rule_ids.join(', ') || '—'}</dd>
                  <dt>Integrations needed</dt>
                  <dd>{pb.required_integrations.join(', ') || 'none'}</dd>
                </dl>
              </Card>
            </div>
          </>
        )
      }
    </Async>
  )
}

function Inspector({
  node,
  actions,
  edges,
  editable,
  onChange,
  onBranchChange,
  onDelete,
  onClose,
}: {
  node: PlaybookNode | null
  actions: ActionSpec[]
  edges: { from: string; to: string; branch?: string | null }[]
  editable: boolean
  onChange: (patch: Partial<PlaybookNode>) => void
  onBranchChange: (to: string, branch: string) => void
  onDelete: () => void
  onClose: () => void
}) {
  if (!node) return null
  const spec = node.action_id ? actions.find((a) => a.id === node.action_id) : undefined

  return (
    <div className="inspector">
      <div className="card-head">
        <h3 style={{ minWidth: 0 }}>{node.label}</h3>
        <div className="spacer" />
        <button className="ghost sm" onClick={onClose} aria-label="Close inspector">
          ✕
        </button>
      </div>
      <div className="card-body">
        <div className="field">
          <label>Label</label>
          <input disabled={!editable} value={node.label} onChange={(e) => onChange({ label: e.target.value })} />
        </div>

        {node.kind === 'action' && (
          <>
            <div className="field">
              <label>Action</label>
              <select disabled={!editable} value={node.action_id ?? ''} onChange={(e) => onChange({ action_id: e.target.value })}>
                {actions.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.category} — {a.name}
                  </option>
                ))}
              </select>
            </div>
            {spec && (
              <>
                <p className="small muted">{spec.description}</p>
                <div className="row wrap" style={{ marginBottom: 10 }}>
                  <Chip tone={spec.requires_approval ? 'warn' : 'neutral'}>{spec.blast_radius}</Chip>
                  {spec.requires_approval && <Chip tone="warn">needs an approval gate</Chip>}
                  {spec.requires_integration && <Chip tone="outline">{spec.requires_integration}</Chip>}
                  {spec.reverses_with && <Chip tone="ok">reversible</Chip>}
                </div>
                {spec.params.length > 0 && (
                  <div className="small muted" style={{ marginBottom: 10 }}>
                    <strong>Parameters</strong>
                    <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
                      {spec.params.map((p) => (
                        <li key={p.name}>
                          <Mono>{p.name}</Mono>
                          {p.required && <span className="chip danger" style={{ marginLeft: 4 }}>required</span>} — {p.description}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
            <label className="row" style={{ gap: 7 }}>
              <input
                type="checkbox"
                disabled={!editable}
                checked={node.continue_on_error ?? false}
                onChange={(e) => onChange({ continue_on_error: e.target.checked })}
              />
              <span className="small">Continue if this fails</span>
            </label>
            <div className="hint small faint" style={{ marginTop: 4 }}>
              Leave off for containment. A run that carries on past a failed containment leaves an
              analyst believing the threat is handled.
            </div>
          </>
        )}

        {node.kind === 'decision' && (
          <>
            <div className="field">
              <label>Question</label>
              <input disabled={!editable} value={node.question ?? ''} onChange={(e) => onChange({ question: e.target.value })} />
            </div>
            <div className="field">
              <label>Expression</label>
              <select disabled={!editable} value={node.expression ?? ''} onChange={(e) => onChange({ expression: e.target.value })}>
                <option value="">always false</option>
                <option value="score>=70">score &gt;= 70</option>
                <option value="score>=80">score &gt;= 80</option>
                <option value="severity>=high">severity &gt;= high</option>
                <option value="severity>=critical">severity &gt;= critical</option>
                <option value="signals>=3">3 or more signals</option>
                <option value="tactic=impact">reached Impact</option>
                <option value="tactic=exfiltration">reached Exfiltration</option>
              </select>
            </div>
            <div className="field">
              <label>Branches</label>
              <input
                disabled={!editable}
                value={(node.branches ?? []).join(', ')}
                onChange={(e) => onChange({ branches: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
              />
              <div className="hint">Every branch needs an outgoing wire, or a run taking it stops mid-response.</div>
            </div>
          </>
        )}

        {node.kind === 'approval' && (
          <>
            <div className="field">
              <label>Prompt</label>
              <input disabled={!editable} value={node.prompt ?? ''} onChange={(e) => onChange({ prompt: e.target.value })} />
            </div>
            <div className="field">
              <label>Approvers</label>
              <input
                disabled={!editable}
                value={(node.approvers ?? []).join(', ')}
                onChange={(e) => onChange({ approvers: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
              />
            </div>
            <div className="field">
              <label>Timeout (minutes)</label>
              <input
                type="number"
                disabled={!editable}
                value={Math.round((node.timeout_ms ?? 0) / 60000)}
                onChange={(e) => onChange({ timeout_ms: Number(e.target.value) * 60000 })}
              />
            </div>
            <div className="field">
              <label>On timeout</label>
              <select disabled={!editable} value={node.on_timeout ?? 'halt'} onChange={(e) => onChange({ on_timeout: e.target.value })}>
                <option value="halt">Halt the run</option>
                <option value="deny">Take the denied branch</option>
                <option value="escalate">Escalate and keep waiting</option>
              </select>
              <div className="hint">There is no auto-approve. An absence is not consent.</div>
            </div>
          </>
        )}

        {node.kind === 'wait' && (
          <div className="field">
            <label>Duration (minutes)</label>
            <input
              type="number"
              disabled={!editable}
              value={Math.round((node.duration_ms ?? 0) / 60000)}
              onChange={(e) => onChange({ duration_ms: Number(e.target.value) * 60000 })}
            />
          </div>
        )}

        {node.kind === 'end' && (
          <div className="field">
            <label>Outcome</label>
            <input disabled={!editable} value={node.outcome ?? ''} onChange={(e) => onChange({ outcome: e.target.value })} />
          </div>
        )}

        {edges.length > 0 && (
          <div className="field">
            <label>Outgoing wires</label>
            {edges.map((e) => (
              <div key={e.to} className="row" style={{ marginBottom: 4 }}>
                <span className="small mono truncate" style={{ flex: 1 }}>
                  → {e.to}
                </span>
                <input
                  disabled={!editable}
                  placeholder="branch"
                  value={e.branch ?? ''}
                  onChange={(ev) => onBranchChange(e.to, ev.target.value)}
                  style={{ width: 96 }}
                />
              </div>
            ))}
          </div>
        )}

        {editable && node.kind !== 'start' && (
          <button className="danger sm" onClick={onDelete}>
            Delete node
          </button>
        )}
      </div>
    </div>
  )
}
